import {
  type DefaultError,
  type UseMutationOptions,
  useMutation,
} from '@tanstack/react-query'
import {createSuggestRepository} from '@yandex-delivery/api/repo/suggest'
import {createSuggestService} from '@yandex-delivery/api/service/suggest'
import {useI18n} from '@yandex-delivery/core/i18n'
import {get} from '@yandex-delivery/utils'
import {useEffect, useMemo, useRef} from 'react'
import {useFormContext, useWatch} from 'react-hook-form'

import {useConfigContext} from '../../../../contexts'
import type {
  LocationDetectedVariant,
  LocationRequest,
  PickupPoint,
  PickupPoints,
} from '../../model/types'
import {withCountryCode, withExternalApiPrefix} from '../../utils/fetchWith'
import {useFetch} from '../fetch'
import {fetchDetectLocationByCity} from './fetch-detect-location'
import {
  createPickupRouteService,
  fetchPickUpDropOffPoints,
  selectPickupPointParams,
} from './index'
import {toPickupPoints} from './mappers/toPickupPoints'
import type {FetchPickupPointsParams} from './types'

const useService = () => {
  const fetch = useFetch()
  const i18n = useI18n()
  const {countryCode} = useConfigContext()

  return useMemo(() => {
    const suggestRepository = createSuggestRepository({
      fetch: withCountryCode(countryCode, withExternalApiPrefix(fetch)),
    })

    return createPickupRouteService({
      params: createSuggestService(suggestRepository),
      fetch,
      countryCode,
      i18n,
    })
  }, [countryCode, fetch, i18n])
}

export const useFetchPickupPoints = (
  options?: Omit<
    UseMutationOptions<PickupPoints, DefaultError, FetchPickupPointsParams>,
    'mutationFn'
  >,
) => {
  const {fetchPickupPoints} = useService()

  return useMutation({...options, mutationFn: fetchPickupPoints})
}

export function useFetchCityDesktop() {
  const fetch = useFetch()
  const {countryCode} = useConfigContext()

  return function fetchCity(params: LocationRequest) {
    return fetchDetectLocationByCity(params, fetch, countryCode)
  }
}

export function useFetchPickupPointsDesktop(isSource: boolean, name: string) {
  const fetch = useFetch()
  const {countryCode} = useConfigContext()
  const {setValue} = useFormContext()
  const i18n = useI18n()
  const isBusiness: boolean = useWatch({name: 'is_business'})
  const point: PickupPoint = useWatch({name: `${name}.pickupPoint`})
  const toPickupPointsBody = selectPickupPointParams(isSource, isBusiness)

  const lastVariantRef = useRef<LocationDetectedVariant | undefined>(undefined)

  const mutation = useMutation({
    mutationFn(variant?: LocationDetectedVariant) {
      lastVariantRef.current = variant

      if (variant === undefined) {
        return Promise.resolve([])
      }

      return fetchPickUpDropOffPoints(
        toPickupPointsBody({
          geo_id: variant.geo_id,
          is_not_branded_partner_station: true,
        }),
        fetch,
        countryCode,
        i18n,
      )
        .then(
          toPickupPoints(
            undefined,
            i18n('ndd.pickup_points.schedule_everyday', 'Ежедневно'),
          ),
        )
        .then(get('features'))
    },

    onSuccess(data) {
      if (point?._id && !data.some(p => p.id === point._id)) {
        setValue(`${name}.pickupPoint`, undefined)
      }
    },
  })

  useEffect(() => {
    if (lastVariantRef.current !== undefined) {
      mutation.mutate(lastVariantRef.current)
    }
  }, [isBusiness])

  return mutation
}

/**
 * Резолвит неполную станцию в полный объект PickupPoint (при предзаполнении)
 */
export function useResolvePickupPoint(name: string, isSource: boolean) {
  const {getValues, setValue} = useFormContext()
  const fetchFn = useFetch()
  const {countryCode} = useConfigContext()
  const i18n = useI18n()
  const isBusiness: boolean = useWatch({name: 'is_business'})

  useEffect(() => {
    const pickupPoint = getValues(`${name}.pickupPoint`)

    // Не запрашиваем станцию если нет id или она уже в стейте формы
    if (!pickupPoint?._id || pickupPoint?.name) return

    const city = getValues(`${name}.city`)
    const body = selectPickupPointParams(isSource, isBusiness)({
      ...(city?.geo_id ? {geo_id: city.geo_id} : {}),
    })

    fetchPickUpDropOffPoints(body, fetchFn, countryCode, i18n)
      .then(
        toPickupPoints(
          undefined,
          i18n('ndd.pickup_points.schedule_everyday', 'Ежедневно'),
        ),
      )
      .then(get('features'))
      .then((features: Array<{pickupPoint: {_id: string}}>) => {
        const match = features?.find(f => f.pickupPoint._id === pickupPoint._id)
        if (match) {
          setValue(`${name}.pickupPoint`, match.pickupPoint)
        } else {
          setValue(`${name}.pickupPoint`, undefined)
        }
      })
      .catch(() => {
        setValue(`${name}.pickupPoint`, undefined)
      })
  }, [])
}
